import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, IconButton, Tooltip,
  Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, CircularProgress, Select, MenuItem, FormControl, InputLabel,
  LinearProgress, Card, CardContent, Divider, Alert, Collapse
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Today, CalendarMonth, DateRange,
  AccessTime, EventAvailable, FreeBreakfast, Restaurant, BeachAccess,
  ArrowBack, OpenInNew, Refresh, KeyboardArrowDown, KeyboardArrowUp,
  CheckCircle, Cancel, HourglassEmpty, ErrorOutline, InfoOutlined,
  Person, Work, Email, SupervisorAccount, Cake, Bloodtype
} from '@mui/icons-material';
import moment from 'moment';
import { getUserAttendance } from '../../api/attendanceAPI';
import { getUserLeaves } from '../../api/leaveAPI';
import { getAllHolidays } from '../../api/holidayAPI';
import { getAllUsers, getSingleUser } from '../../api/userAPI';

const LEAVE_QUOTAS = {
  Casual: 10,
  Sick: 7,
  Planned: 8,
  Paid: 8
};

// Format seconds into "Xh Ym" or "Ym Zs"
const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  if (m > 0) {
    return `${m}m ${s > 0 ? s + 's' : ''}`.trim();
  }
  return `${s}s`;
};

// Calculate financial year start and end for a given moment
const getFinancialYearRange = (anchorDate = moment()) => {
  let startYear = anchorDate.year();
  let endYear = anchorDate.year() + 1;
  if (anchorDate.month() < 3) { // Jan=0, Feb=1, Mar=2
    startYear = anchorDate.year() - 1;
    endYear = anchorDate.year();
  }
  const fyStart = moment(`${startYear}-04-01`).startOf('day');
  const fyEnd = moment(`${endYear}-03-31`).endOf('day');
  return { fyStart, fyEnd, label: `FY ${startYear}-${String(endYear).slice(-2)}` };
};

const UserSummaryContent = ({
  userId,
  initialUser = null,
  allUsers = [],
  isModal = false,
  onClose = null,
  onOpenFullPage = null,
  onUserChange = null
}) => {
  // Current user state
  const [currentUser, setCurrentUser] = useState(initialUser);
  const [activeUserId, setActiveUserId] = useState(userId || initialUser?.id || initialUser?._id);
  const [usersList, setUsersList] = useState(allUsers || []);

  // Data state
  const [loading, setLoading] = useState(true);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [leavesList, setLeavesList] = useState([]);
  const [holidaysList, setHolidaysList] = useState([]);

  // Filter state
  const [filterMode, setFilterMode] = useState('month'); // 'day', 'week', 'month', 'year', 'custom'
  const [anchorDate, setAnchorDate] = useState(moment());
  const [customStart, setCustomStart] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [customEnd, setCustomEnd] = useState(moment().format('YYYY-MM-DD'));

  // Active sub-tab
  const [activeTab, setActiveTab] = useState(0);

  // Expandable rows in attendance table (for break details)
  const [expandedDates, setExpandedDates] = useState({});

  const toggleDateExpand = (dateStr) => {
    setExpandedDates(prev => ({ ...prev, [dateStr]: !prev[dateStr] }));
  };

  // Sync prop changes
  useEffect(() => {
    if (userId && userId !== activeUserId) {
      setActiveUserId(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (initialUser) {
      setCurrentUser(initialUser);
    }
  }, [initialUser]);

  // Load user list if empty
  useEffect(() => {
    if (!usersList || usersList.length === 0) {
      getAllUsers()
        .then(res => {
          if (res.data) setUsersList(res.data);
        })
        .catch(err => console.error("Error loading users list:", err));
    }
  }, []);

  // Load current user details if not provided
  useEffect(() => {
    if (!activeUserId) return;
    const foundInList = usersList.find(u => (u.id || u._id) === activeUserId);
    if (foundInList) {
      setCurrentUser(foundInList);
    } else {
      getSingleUser(activeUserId)
        .then(res => {
          const uData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (uData) setCurrentUser(uData);
        })
        .catch(err => console.error("Error fetching single user:", err));
    }
  }, [activeUserId, usersList]);

  // Fetch Attendance, Leaves, Holidays
  const loadUserData = async () => {
    if (!activeUserId) return;
    setLoading(true);
    try {
      const [attRes, leaveRes, holRes] = await Promise.all([
        getUserAttendance(activeUserId),
        getUserLeaves(activeUserId),
        getAllHolidays()
      ]);

      if (attRes.data && attRes.data.success) {
        setAttendanceLogs(attRes.data.log || []);
      }
      if (leaveRes.data && leaveRes.data.success) {
        setLeavesList(leaveRes.data.leaves || []);
      }
      if (holRes.data && holRes.data.success) {
        setHolidaysList(holRes.data.holidays || []);
      }
    } catch (err) {
      console.error("Error loading user summary data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [activeUserId]);

  const handleSelectUser = (e) => {
    const newId = e.target.value;
    setActiveUserId(newId);
    if (onUserChange) onUserChange(newId);
  };

  // Determine current active date range based on filterMode and anchorDate
  const { dateRange, rangeLabel } = useMemo(() => {
    let start, end, label;
    if (filterMode === 'day') {
      start = anchorDate.clone().startOf('day');
      end = anchorDate.clone().endOf('day');
      label = anchorDate.format('dddd, DD MMMM YYYY');
    } else if (filterMode === 'week') {
      start = anchorDate.clone().startOf('isoWeek');
      end = anchorDate.clone().endOf('isoWeek');
      label = `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')} (Week ${anchorDate.isoWeek()})`;
    } else if (filterMode === 'month') {
      start = anchorDate.clone().startOf('month');
      end = anchorDate.clone().endOf('month');
      label = anchorDate.format('MMMM YYYY');
    } else if (filterMode === 'year') {
      const { fyStart, fyEnd, label: fyLabel } = getFinancialYearRange(anchorDate);
      start = fyStart;
      end = fyEnd;
      label = `${fyLabel} (${fyStart.format('DD MMM YYYY')} - ${fyEnd.format('DD MMM YYYY')})`;
    } else {
      // custom
      start = moment(customStart).startOf('day');
      end = moment(customEnd).endOf('day');
      label = `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
    }
    return { dateRange: { start, end }, rangeLabel: label };
  }, [filterMode, anchorDate, customStart, customEnd]);

  // Step backward / forward
  const handlePrev = () => {
    if (filterMode === 'day') setAnchorDate(prev => prev.clone().subtract(1, 'day'));
    else if (filterMode === 'week') setAnchorDate(prev => prev.clone().subtract(1, 'week'));
    else if (filterMode === 'month') setAnchorDate(prev => prev.clone().subtract(1, 'month'));
    else if (filterMode === 'year') setAnchorDate(prev => prev.clone().subtract(1, 'year'));
  };

  const handleNext = () => {
    if (filterMode === 'day') setAnchorDate(prev => prev.clone().add(1, 'day'));
    else if (filterMode === 'week') setAnchorDate(prev => prev.clone().add(1, 'week'));
    else if (filterMode === 'month') setAnchorDate(prev => prev.clone().add(1, 'month'));
    else if (filterMode === 'year') setAnchorDate(prev => prev.clone().add(1, 'year'));
  };

  const handleJumpToCurrent = () => {
    setAnchorDate(moment());
  };

  // ==========================================
  // AGGREGATIONS & METRICS CALCULATION
  // ==========================================
  const metrics = useMemo(() => {
    const { start, end } = dateRange;

    // Filter attendance logs in range
    const filteredAttendance = attendanceLogs.filter(a => {
      if (!a.date) return false;
      const mDate = moment(a.date);
      return mDate.isBetween(start, end, 'day', '[]');
    });

    // Sort descending by date
    filteredAttendance.sort((a, b) => moment(b.date).diff(moment(a.date)));

    let presentDays = 0;
    let halfDays = 0;
    let absentDays = 0;
    let lateCheckIns = 0;

    let totalWorkSeconds = 0;
    let daysWithWorkTime = 0;

    let totalLunchSeconds = 0;
    let lunchCount = 0;
    let overtimeLunchCount = 0; // > 30m

    let totalShortBreakSeconds = 0;
    let totalShortBreaksCount = 0;
    let daysWithShortBreaks = 0;

    // Map by date for quick lookup
    const attendanceMap = new Map();
    filteredAttendance.forEach(att => {
      attendanceMap.set(att.date, att);

      // Status counts
      if (att.status === 'Half-day') {
        halfDays++;
      } else if (att.status === 'Absent') {
        absentDays++;
      } else if (att.status === 'Present' || (att.checkIn && att.status !== 'Absent')) {
        presentDays++;
      }

      // Late check-in detection (check-in after 9:35 AM IST)
      if (att.checkIn) {
        const cIn = moment(att.checkIn);
        const minutesFromMidnight = cIn.hours() * 60 + cIn.minutes();
        if (att.status === 'Late' || minutesFromMidnight > (9 * 60 + 35)) {
          lateCheckIns++;
        }
      }

      // Lunch calculation
      let dayLunchSec = 0;
      if (att.lunchStart) {
        lunchCount++;
        const lEnd = att.lunchEnd ? moment(att.lunchEnd) : moment();
        dayLunchSec = Math.max(0, lEnd.diff(moment(att.lunchStart), 'seconds'));
        totalLunchSeconds += dayLunchSec;
        if (dayLunchSec > (30 * 60)) {
          overtimeLunchCount++;
        }
      }

      // Short breaks calculation
      let dayShortBreakSec = 0;
      if (att.shortBreaks && att.shortBreaks.length > 0) {
        daysWithShortBreaks++;
        att.shortBreaks.forEach(b => {
          totalShortBreaksCount++;
          const bEnd = b.end ? moment(b.end) : moment();
          const dur = Math.max(0, bEnd.diff(moment(b.start), 'seconds'));
          dayShortBreakSec += dur;
        });
        totalShortBreakSeconds += dayShortBreakSec;
      }

      // Net working time
      if (att.checkIn) {
        const cIn = moment(att.checkIn);
        const isToday = att.date === moment().format('YYYY-MM-DD');
        const cOut = att.checkOut ? moment(att.checkOut) : (isToday ? moment() : null);

        if (cOut) {
          const grossSec = Math.max(0, cOut.diff(cIn, 'seconds'));
          const netWorkSec = Math.max(0, grossSec - dayLunchSec - dayShortBreakSec);
          totalWorkSeconds += netWorkSec;
          daysWithWorkTime++;
        }
      }
    });

    const avgWorkSeconds = daysWithWorkTime > 0 ? Math.round(totalWorkSeconds / daysWithWorkTime) : 0;
    const avgLunchSeconds = lunchCount > 0 ? Math.round(totalLunchSeconds / lunchCount) : 0;
    const avgShortBreakSeconds = totalShortBreaksCount > 0 ? Math.round(totalShortBreakSeconds / totalShortBreaksCount) : 0;
    const totalBreakSeconds = totalLunchSeconds + totalShortBreakSeconds;

    // Filter leaves in range
    const filteredLeaves = leavesList.filter(l => {
      if (l.status === 'Cancelled') return false;
      if (l.dates && l.dates.length > 0) {
        return l.dates.some(d => moment(d).isBetween(start, end, 'day', '[]'));
      }
      if (l.startDate && l.endDate) {
        return moment(l.startDate).isSameOrBefore(end, 'day') && moment(l.endDate).isSameOrAfter(start, 'day');
      }
      return false;
    });

    let periodCasualLeaves = 0;
    let periodSickLeaves = 0;
    let periodPlannedLeaves = 0;
    let periodPaidLeaves = 0;
    let periodUnpaidLeaves = 0;
    let periodApprovedLeavesCount = 0;

    filteredLeaves.forEach(l => {
      const datesInRange = (l.dates || []).filter(d => moment(d).isBetween(start, end, 'day', '[]'));
      const count = datesInRange.length > 0 ? datesInRange.length : 1;

      if (l.status === 'Approved') {
        periodApprovedLeavesCount += count;
        if (l.type === 'Casual') periodCasualLeaves += count;
        else if (l.type === 'Sick') periodSickLeaves += count;
        else if (l.type === 'Planned' || l.type === 'Paid' || l.type === 'Earned') {
          periodPlannedLeaves += count;
          periodPaidLeaves += count;
        }
        else if (l.type === 'Unpaid') periodUnpaidLeaves += count;
      }
    });

    // Financial Year Annual Leave Quota Calculation
    const { fyStart, fyEnd } = getFinancialYearRange(moment());
    let fyCasualUsed = 0;
    let fySickUsed = 0;
    let fyPlannedUsed = 0;
    let fyPaidUsed = 0;
    let fyUnpaidUsed = 0;

    leavesList.forEach(l => {
      if (l.status !== 'Approved') return;
      const validDates = (l.dates || []).filter(d =>
        moment(d).isBetween(fyStart, fyEnd, 'day', '[]') &&
        !holidaysList.some(h => moment(h.date).isSame(moment(d), 'day'))
      );
      const count = validDates.length > 0 ? validDates.length : (
        (l.startDate && moment(l.startDate).isBetween(fyStart, fyEnd, 'day', '[]')) ? 1 : 0
      );

      if (l.type === 'Casual') fyCasualUsed += count;
      else if (l.type === 'Sick') fySickUsed += count;
      else if (l.type === 'Planned' || l.type === 'Paid' || l.type === 'Earned') {
        fyPlannedUsed += count;
        fyPaidUsed += count;
      }
      else if (l.type === 'Unpaid') fyUnpaidUsed += count;
    });

    const totalAnnualQuota = LEAVE_QUOTAS.Casual + LEAVE_QUOTAS.Sick + (LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid);
    const totalAnnualUsed = fyCasualUsed + fySickUsed + fyPlannedUsed;
    const annualBalanceRemaining = Math.max(0, totalAnnualQuota - totalAnnualUsed);

    // Total working days in period (excluding Sundays and holidays)
    let totalCalendarDays = Math.max(1, end.diff(start, 'days') + 1);
    let workingDaysCount = 0;
    let cur = start.clone();
    while (cur.isSameOrBefore(end, 'day')) {
      const isSunday = cur.day() === 0;
      const isHoliday = holidaysList.some(h => moment(h.date).isSame(cur, 'day'));
      if (!isSunday && !isHoliday) {
        workingDaysCount++;
      }
      cur.add(1, 'day');
    }

    const attendanceRate = workingDaysCount > 0
      ? Math.min(100, Math.round(((presentDays + halfDays * 0.5) / workingDaysCount) * 100))
      : 100;

    return {
      filteredAttendance,
      filteredLeaves,
      totalCalendarDays,
      workingDaysCount,
      presentDays,
      halfDays,
      absentDays,
      lateCheckIns,
      attendanceRate,
      totalWorkSeconds,
      avgWorkSeconds,
      totalLunchSeconds,
      lunchCount,
      avgLunchSeconds,
      overtimeLunchCount,
      totalShortBreakSeconds,
      totalShortBreaksCount,
      daysWithShortBreaks,
      avgShortBreakSeconds,
      totalBreakSeconds,
      // Period leaves
      periodCasualLeaves,
      periodSickLeaves,
      periodPlannedLeaves,
      periodPaidLeaves,
      periodUnpaidLeaves,
      periodApprovedLeavesCount,
      // Annual FY
      fyCasualUsed,
      fySickUsed,
      fyPlannedUsed,
      fyPaidUsed,
      fyUnpaidUsed,
      totalAnnualQuota,
      totalAnnualUsed,
      annualBalanceRemaining,
      attendanceMap
    };
  }, [attendanceLogs, leavesList, holidaysList, dateRange]);

  // Extract all individual breaks for Break Analytics tab
  const allBreakSessions = useMemo(() => {
    const list = [];
    metrics.filteredAttendance.forEach(att => {
      if (att.lunchStart) {
        const lEnd = att.lunchEnd ? moment(att.lunchEnd) : (att.date === moment().format('YYYY-MM-DD') ? moment() : null);
        const dur = lEnd ? Math.max(0, lEnd.diff(moment(att.lunchStart), 'seconds')) : 0;
        list.push({
          date: att.date,
          type: 'Lunch Break',
          start: att.lunchStart,
          end: att.lunchEnd,
          duration: dur,
          isOngoing: !att.lunchEnd && att.date === moment().format('YYYY-MM-DD'),
          isOvertime: dur > (30 * 60)
        });
      }
      if (att.shortBreaks && att.shortBreaks.length > 0) {
        att.shortBreaks.forEach((b, idx) => {
          const bEnd = b.end ? moment(b.end) : (att.date === moment().format('YYYY-MM-DD') ? moment() : null);
          const dur = bEnd ? Math.max(0, bEnd.diff(moment(b.start), 'seconds')) : 0;
          list.push({
            date: att.date,
            type: `Short Break #${idx + 1}`,
            start: b.start,
            end: b.end,
            duration: dur,
            isOngoing: !b.end && att.date === moment().format('YYYY-MM-DD'),
            isOvertime: dur > (15 * 60) // alert if single short break > 15m
          });
        });
      }
    });

    list.sort((a, b) => moment(b.date + ' ' + (b.start ? moment(b.start).format('HH:mm:ss') : '00:00:00'))
      .diff(moment(a.date + ' ' + (a.start ? moment(a.start).format('HH:mm:ss') : '00:00:00'))));
    return list;
  }, [metrics.filteredAttendance]);

  // Work vs Break Ratio calculations
  const totalActivityTime = metrics.totalWorkSeconds + metrics.totalBreakSeconds;
  const workPercent = totalActivityTime > 0 ? Math.round((metrics.totalWorkSeconds / totalActivityTime) * 100) : 0;
  const lunchPercent = totalActivityTime > 0 ? Math.round((metrics.totalLunchSeconds / totalActivityTime) * 100) : 0;
  const shortBreakPercent = totalActivityTime > 0 ? Math.max(0, 100 - workPercent - lunchPercent) : 0;

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* ==================================================== */}
      {/* 1. TOP HEADER & NAVIGATION BAR                      */}
      {/* ==================================================== */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {!isModal && (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => window.history.back()}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Back to Users
            </Button>
          )}

          <Typography variant="h5" fontWeight="800" sx={{ color: '#1e293b' }}>
            User Summary & Analytics
          </Typography>

          {isModal && onOpenFullPage && (
            <Tooltip title="Open in dedicated full page">
              <IconButton color="primary" onClick={onOpenFullPage} size="small">
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Employee Switcher */}
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="user-select-label">Select Employee</InputLabel>
            <Select
              labelId="user-select-label"
              value={activeUserId || ''}
              label="Select Employee"
              onChange={handleSelectUser}
              sx={{ borderRadius: 2 }}
            >
              {usersList.map(u => (
                <MenuItem key={u.id || u._id} value={u.id || u._id}>
                  {u.fullName || u.email} ({u.permission || 'User'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Refresh user data">
            <IconButton onClick={loadUserData} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>

          {isModal && onClose && (
            <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
              Close
            </Button>
          )}
        </Box>
      </Box>

      {/* ==================================================== */}
      {/* 2. USER PROFILE BANNER                              */}
      {/* ==================================================== */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(30, 60, 114, 0.2)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                fontSize: '2rem',
                fontWeight: 'bold',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                border: '3px solid rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(4px)'
              }}
            >
              {currentUser?.fullName ? currentUser.fullName.charAt(0).toUpperCase() : <Person />}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: 'white', letterSpacing: '-0.3px' }}>
                  {currentUser?.fullName || 'Loading user...'}
                </Typography>
                <Chip
                  label={currentUser?.permission || 'User'}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 'bold' }}
                />
                {currentUser?.bloodGroup && (
                  <Chip
                    icon={<Bloodtype sx={{ '&&': { color: '#fff' } }} />}
                    label={`Blood: ${currentUser.bloodGroup}`}
                    size="small"
                    sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 'bold', '& .MuiChip-icon': { color: '#fff' } }}
                  />
                )}
                {currentUser?.isOfficeEmp && (
                  <Chip
                    label="Office Emp"
                    size="small"
                    sx={{ bgcolor: '#4caf50', color: '#fff', fontWeight: 'bold' }}
                  />
                )}
                {currentUser?.isActive === false && (
                  <Chip
                    label="Inactive"
                    size="small"
                    sx={{ bgcolor: '#f44336', color: '#fff', fontWeight: 'bold' }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mt: 1, flexWrap: 'wrap', opacity: 0.9 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Email sx={{ fontSize: 16 }} />
                  <Typography variant="body2">{currentUser?.email || 'N/A'}</Typography>
                </Box>
                {currentUser?.department && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Work sx={{ fontSize: 16 }} />
                    <Typography variant="body2">{currentUser.department}</Typography>
                  </Box>
                )}
                {currentUser?.teamLeader && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <SupervisorAccount sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      Leader: {typeof currentUser.teamLeader === 'object' ? currentUser.teamLeader.fullName : 'Assigned'}
                    </Typography>
                  </Box>
                )}
                {(currentUser?.dob || currentUser?.dateOfBirth) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Cake sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      DOB: {moment(currentUser.dob || currentUser.dateOfBirth).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                )}
                {currentUser?.officeJoiningDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <EventAvailable sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      Joined: {moment(currentUser.officeJoiningDate).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Quick Annual Quota Snapshot */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              p: 1.5,
              borderRadius: 2,
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box sx={{ textAlign: 'center', px: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Casual (CL)</Typography>
              <Typography variant="h6" fontWeight="bold">{metrics.fyCasualUsed} / {LEAVE_QUOTAS.Casual}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Box sx={{ textAlign: 'center', px: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Sick (SL)</Typography>
              <Typography variant="h6" fontWeight="bold">{metrics.fySickUsed} / {LEAVE_QUOTAS.Sick}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Box sx={{ textAlign: 'center', px: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Planned (PL)</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#a5d6a7' }}>
                {metrics.fyPlannedUsed ?? metrics.fyPaidUsed} / {LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Box sx={{ textAlign: 'center', px: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Available</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff9c4' }}>
                {metrics.annualBalanceRemaining} Days
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ==================================================== */}
      {/* 3. TIME PERIOD & FILTER TOOLBAR                      */}
      {/* ==================================================== */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          bgcolor: 'white',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* Mode Selector Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { id: 'day', label: 'Day', icon: <Today sx={{ fontSize: 18 }} /> },
              { id: 'week', label: 'Week', icon: <DateRange sx={{ fontSize: 18 }} /> },
              { id: 'month', label: 'Month', icon: <CalendarMonth sx={{ fontSize: 18 }} /> },
              { id: 'year', label: 'Financial Year', icon: <EventAvailable sx={{ fontSize: 18 }} /> },
              { id: 'custom', label: 'Custom Range', icon: <AccessTime sx={{ fontSize: 18 }} /> }
            ].map(m => (
              <Button
                key={m.id}
                size="small"
                variant={filterMode === m.id ? 'contained' : 'outlined'}
                onClick={() => setFilterMode(m.id)}
                startIcon={m.icon}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: filterMode === m.id ? '#1976d2' : 'transparent',
                  color: filterMode === m.id ? '#fff' : '#64748b',
                  borderColor: filterMode === m.id ? '#1976d2' : '#cbd5e1',
                  '&:hover': {
                    bgcolor: filterMode === m.id ? '#1565c0' : '#f1f5f9'
                  }
                }}
              >
                {m.label}
              </Button>
            ))}
          </Box>

          {/* Stepper Navigation (For Day, Week, Month, Year) */}
          {filterMode !== 'custom' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePrev} size="small" sx={{ border: '1px solid #e2e8f0' }}>
                <ChevronLeft />
              </IconButton>

              <Typography variant="subtitle2" fontWeight="700" sx={{ minWidth: 220, textAlign: 'center', color: '#1e293b' }}>
                {rangeLabel}
              </Typography>

              <IconButton onClick={handleNext} size="small" sx={{ border: '1px solid #e2e8f0' }}>
                <ChevronRight />
              </IconButton>

              <Button
                size="small"
                variant="text"
                onClick={handleJumpToCurrent}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Current
              </Button>
            </Box>
          ) : (
            /* Custom Range Inputs */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem'
                }}
              />
              <Typography variant="body2" color="text.secondary">to</Typography>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem'
                }}
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Filter Summary Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlined sx={{ fontSize: 16, color: '#64748b' }} />
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
              Period: <b>{rangeLabel}</b> ({metrics.totalCalendarDays} calendar days, <b>{metrics.workingDaysCount} working days</b>)
            </Typography>
          </Box>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading records...</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ==================================================== */}
      {/* 4. KEY SUMMARY METRICS (5 KPI CARDS)                */}
      {/* ==================================================== */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Card 1: Attendance */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 15px rgba(25, 118, 210, 0.1)', borderColor: '#90caf9' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#e3f2fd', color: '#1976d2' }}>
                <EventAvailable sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight="700" color="text.secondary">Attendance</Typography>
            </Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a' }}>
              {metrics.presentDays} <Typography component="span" variant="body2" color="text.secondary">/ {metrics.workingDaysCount} Days</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={metrics.attendanceRate}
                sx={{
                  flexGrow: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: metrics.attendanceRate >= 85 ? '#2e7d32' : metrics.attendanceRate >= 70 ? '#f57c00' : '#d32f2f'
                  }
                }}
              />
              <Typography variant="caption" fontWeight="bold" color="text.secondary">{metrics.attendanceRate}%</Typography>
            </Box>
            <Typography variant="caption" display="block" color="text.secondary">
              Half-days: <b>{metrics.halfDays}</b> | Late: <b>{metrics.lateCheckIns}</b>
            </Typography>
          </Card>
        </Grid>

        {/* Card 2: Work Hours */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 15px rgba(103, 58, 183, 0.1)', borderColor: '#b39ddb' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#ede7f6', color: '#673ab7' }}>
                <AccessTime sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight="700" color="text.secondary">Work Duration</Typography>
            </Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a' }}>
              {formatDuration(metrics.totalWorkSeconds)}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Avg: <b>{formatDuration(metrics.avgWorkSeconds)} / present day</b>
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
              Net productive work time
            </Typography>
          </Card>
        </Grid>

        {/* Card 3: Lunch Break */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 15px rgba(237, 108, 2, 0.1)', borderColor: '#ffcc80' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#fff3e0', color: '#ed6c02' }}>
                <Restaurant sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight="700" color="text.secondary">Lunch Breaks</Typography>
            </Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a' }}>
              {formatDuration(metrics.totalLunchSeconds)}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Avg: <b>{formatDuration(metrics.avgLunchSeconds)} / session</b>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Chip
                label={metrics.overtimeLunchCount > 0 ? `${metrics.overtimeLunchCount} exceeded >30m` : 'Normal duration'}
                size="small"
                color={metrics.overtimeLunchCount > 0 ? 'warning' : 'success'}
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>
          </Card>
        </Grid>

        {/* Card 4: Short Breaks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 15px rgba(2, 136, 209, 0.1)', borderColor: '#81d4fa' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#e1f5fe', color: '#0288d1' }}>
                <FreeBreakfast sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight="700" color="text.secondary">Short Breaks</Typography>
            </Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a' }}>
              {formatDuration(metrics.totalShortBreakSeconds)}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              <b>{metrics.totalShortBreaksCount}</b> breaks across {metrics.daysWithShortBreaks} days
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: '#0288d1', fontWeight: 600 }}>
              Avg: {formatDuration(metrics.avgShortBreakSeconds)} / break
            </Typography>
          </Card>
        </Grid>

        {/* Card 5: Leaves */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 15px rgba(46, 125, 50, 0.1)', borderColor: '#a5d6a7' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#e8f5e9', color: '#2e7d32' }}>
                <BeachAccess sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight="700" color="text.secondary">Leaves</Typography>
            </Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a' }}>
              {metrics.periodApprovedLeavesCount} <Typography component="span" variant="body2" color="text.secondary">in period</Typography>
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              CL: <b>{metrics.periodCasualLeaves}</b> | SL: <b>{metrics.periodSickLeaves}</b> | PL: <b>{metrics.periodPlannedLeaves ?? metrics.periodPaidLeaves}</b>
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: '#2e7d32', fontWeight: 600 }}>
              Annual Left: <b>{metrics.annualBalanceRemaining} / {metrics.totalAnnualQuota}</b>
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* ==================================================== */}
      {/* 5. WORK TIME VS BREAK TIME PROPORTION BAR           */}
      {/* ==================================================== */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          bgcolor: 'white',
          border: '1px solid #e2e8f0'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="700" color="text.secondary">
            Productivity Ratio (Logged Time Breakdown)
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">
            Total Logged: {formatDuration(totalActivityTime)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', bgcolor: '#f1f5f9', mb: 1.5 }}>
          <Box sx={{ width: `${workPercent}%`, bgcolor: '#1976d2' }} title={`Work: ${workPercent}%`} />
          <Box sx={{ width: `${lunchPercent}%`, bgcolor: '#ed6c02' }} title={`Lunch: ${lunchPercent}%`} />
          <Box sx={{ width: `${shortBreakPercent}%`, bgcolor: '#0288d1' }} title={`Short Breaks: ${shortBreakPercent}%`} />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: '0.8rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1976d2' }} />
            <Typography variant="caption">Productive Work: <b>{formatDuration(metrics.totalWorkSeconds)} ({workPercent}%)</b></Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ed6c02' }} />
            <Typography variant="caption">Lunch Breaks: <b>{formatDuration(metrics.totalLunchSeconds)} ({lunchPercent}%)</b></Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#0288d1' }} />
            <Typography variant="caption">Short Breaks: <b>{formatDuration(metrics.totalShortBreakSeconds)} ({shortBreakPercent}%)</b></Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="caption" fontWeight="bold" color="secondary">
              Combined Total Breaks: {formatDuration(metrics.totalBreakSeconds)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ==================================================== */}
      {/* 6. DETAILED DATA DRILLDOWN TABS                      */}
      {/* ==================================================== */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          bgcolor: 'white',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            px: 2,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.95rem' }
          }}
        >
          <Tab label={`Daily Attendance & Breaks Log (${metrics.filteredAttendance.length})`} />
          <Tab label={`Leave History & Balances (${metrics.filteredLeaves.length})`} />
          <Tab label={`Break Log & Timeline (${allBreakSessions.length})`} />
        </Tabs>

        {/* TAB 0: DAILY ATTENDANCE & BREAKS LOG */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            {metrics.filteredAttendance.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ErrorOutline sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                  No attendance records found for this period.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Try adjusting the date filter above (e.g. Month or Financial Year).
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f1f5f9', fontWeight: 'bold', color: '#334155' } }}>
                      <TableCell width={40}></TableCell>
                      <TableCell><b>Date & Day</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Check-In</b></TableCell>
                      <TableCell><b>Check-Out</b></TableCell>
                      <TableCell><b>Net Work</b></TableCell>
                      <TableCell><b>Lunch Break</b></TableCell>
                      <TableCell><b>Short Breaks</b></TableCell>
                      <TableCell><b>Total Breaks</b></TableCell>
                      <TableCell><b>Remarks / Early Reason</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.filteredAttendance.map((att) => {
                      const mDate = moment(att.date);
                      const isExpanded = !!expandedDates[att.date];
                      const hasBreaks = (att.shortBreaks && att.shortBreaks.length > 0) || att.lunchStart;

                      // Calculate lunch duration
                      let lunchDurSec = 0;
                      if (att.lunchStart) {
                        const lEnd = att.lunchEnd ? moment(att.lunchEnd) : (att.date === moment().format('YYYY-MM-DD') ? moment() : null);
                        lunchDurSec = lEnd ? Math.max(0, lEnd.diff(moment(att.lunchStart), 'seconds')) : 0;
                      }

                      // Calculate short breaks duration
                      let shortBreakSec = 0;
                      if (att.shortBreaks) {
                        att.shortBreaks.forEach(b => {
                          const bEnd = b.end ? moment(b.end) : (att.date === moment().format('YYYY-MM-DD') ? moment() : null);
                          shortBreakSec += bEnd ? Math.max(0, bEnd.diff(moment(b.start), 'seconds')) : 0;
                        });
                      }

                      // Net work time
                      let netWorkSec = 0;
                      if (att.checkIn) {
                        const cIn = moment(att.checkIn);
                        const isToday = att.date === moment().format('YYYY-MM-DD');
                        const cOut = att.checkOut ? moment(att.checkOut) : (isToday ? moment() : null);
                        if (cOut) {
                          const gross = Math.max(0, cOut.diff(cIn, 'seconds'));
                          netWorkSec = Math.max(0, gross - lunchDurSec - shortBreakSec);
                        }
                      }

                      const totalDayBreakSec = lunchDurSec + shortBreakSec;

                      return (
                        <React.Fragment key={att._id || att.date}>
                          <TableRow
                            hover
                            sx={{
                              '&:hover': { bgcolor: '#f8fafc' },
                              bgcolor: isExpanded ? '#f1f5f9' : 'inherit'
                            }}
                          >
                            <TableCell>
                              {hasBreaks ? (
                                <IconButton size="small" onClick={() => toggleDateExpand(att.date)}>
                                  {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                </IconButton>
                              ) : null}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight="600" sx={{ color: '#0f172a' }}>
                                {mDate.format('DD MMM YYYY')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {mDate.format('dddd')}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={att.status || 'Present'}
                                size="small"
                                color={
                                  att.status === 'Absent' ? 'error' :
                                  att.status === 'Half-day' ? 'warning' : 'success'
                                }
                                sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}
                              />
                              {att.halfDayStatus && att.halfDayStatus !== 'N/A' && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  ({att.halfDayStatus})
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {att.checkIn ? (
                                <Box>
                                  <Typography variant="body2">{moment(att.checkIn).format('hh:mm A')}</Typography>
                                  {att.status === 'Late' && (
                                    <Chip label="Late" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                </Box>
                              ) : '-'}
                            </TableCell>

                            <TableCell>
                              {att.checkOut ? (
                                <Typography variant="body2">{moment(att.checkOut).format('hh:mm A')}</Typography>
                              ) : (
                                att.date === moment().format('YYYY-MM-DD') ? (
                                  <Chip label="Active / In Office" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                                ) : '-'
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: '#1976d2' }}>
                                {netWorkSec > 0 ? formatDuration(netWorkSec) : '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {att.lunchStart ? (
                                <Box>
                                  <Typography variant="body2" fontWeight="600" sx={{ color: lunchDurSec > 1800 ? '#d32f2f' : '#ed6c02' }}>
                                    {formatDuration(lunchDurSec)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {moment(att.lunchStart).format('hh:mm A')} - {att.lunchEnd ? moment(att.lunchEnd).format('hh:mm A') : 'Ongoing'}
                                  </Typography>
                                </Box>
                              ) : '-'}
                            </TableCell>

                            <TableCell>
                              {att.shortBreaks && att.shortBreaks.length > 0 ? (
                                <Box>
                                  <Typography variant="body2" fontWeight="600" sx={{ color: '#0288d1' }}>
                                    {formatDuration(shortBreakSec)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {att.shortBreaks.length} {att.shortBreaks.length === 1 ? 'break' : 'breaks'}
                                  </Typography>
                                </Box>
                              ) : '-'}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {totalDayBreakSec > 0 ? formatDuration(totalDayBreakSec) : '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {att.earlyCheckoutReason || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Expandable Breakdown of Breaks for that Day */}
                          {hasBreaks && (
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1.5, px: 3, bgcolor: '#f8fafc', borderRadius: 2, my: 1, border: '1px dashed #cbd5e1' }}>
                                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#475569', textTransform: 'uppercase' }}>
                                      Detailed Break Breakdown for {mDate.format('DD MMM YYYY')}
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                      {att.lunchStart && (
                                        <Grid item xs={12} sm={6}>
                                          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #ffcc80', bgcolor: '#fff8e1', borderRadius: 2 }}>
                                            <Typography variant="body2" fontWeight="bold" color="#ed6c02">
                                              Lunch Break ({formatDuration(lunchDurSec)})
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              Start: {moment(att.lunchStart).format('hh:mm:ss A')}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              End: {att.lunchEnd ? moment(att.lunchEnd).format('hh:mm:ss A') : 'Ongoing'}
                                            </Typography>
                                            {lunchDurSec > 1800 && (
                                              <Typography variant="caption" color="error" fontWeight="bold" display="block">
                                                Exceeded 30-min standard lunch limit!
                                              </Typography>
                                            )}
                                          </Paper>
                                        </Grid>
                                      )}

                                      {att.shortBreaks && att.shortBreaks.map((sb, sbIdx) => {
                                        const sbEnd = sb.end ? moment(sb.end) : (att.date === moment().format('YYYY-MM-DD') ? moment() : null);
                                        const sbDur = sbEnd ? Math.max(0, sbEnd.diff(moment(sb.start), 'seconds')) : 0;
                                        return (
                                          <Grid item xs={12} sm={6} md={4} key={sbIdx}>
                                            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #b3e5fc', bgcolor: '#f0f9ff', borderRadius: 2 }}>
                                              <Typography variant="body2" fontWeight="bold" color="#0288d1">
                                                Short Break #{sbIdx + 1} ({formatDuration(sbDur)})
                                              </Typography>
                                              <Typography variant="caption" display="block">
                                                Start: {moment(sb.start).format('hh:mm:ss A')}
                                              </Typography>
                                              <Typography variant="caption" display="block">
                                                End: {sb.end ? moment(sb.end).format('hh:mm:ss A') : 'Ongoing'}
                                              </Typography>
                                            </Paper>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* TAB 1: LEAVE HISTORY & BALANCES */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Visual Quota Progress Bars */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Annual Leave Quota Status ({getFinancialYearRange(moment()).label})
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { label: 'Casual Leave (CL)', used: metrics.fyCasualUsed, quota: LEAVE_QUOTAS.Casual, color: '#ffa726', desc: '10 days standard' },
                { label: 'Sick Leave (SL)', used: metrics.fySickUsed, quota: LEAVE_QUOTAS.Sick, color: '#ef5350', desc: '7 days medical' },
                { label: 'Planned Leave (PL)', used: metrics.fyPlannedUsed ?? metrics.fyPaidUsed, quota: LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid, color: '#66bb6a', desc: '8 days planned' }
              ].map(card => {
                const rem = Math.max(0, card.quota - card.used);
                const percent = Math.min(100, Math.round((card.used / card.quota) * 100));
                return (
                  <Grid item xs={12} sm={4} key={card.label}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: `1px solid ${card.color}50`,
                        bgcolor: `${card.color}08`
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{card.label}</Typography>
                        <Chip
                          label={`${rem} Left`}
                          size="small"
                          sx={{ bgcolor: card.color, color: '#fff', fontWeight: 'bold' }}
                        />
                      </Box>
                      <Typography variant="h5" fontWeight="800" sx={{ color: card.color, my: 0.5 }}>
                        {card.used} <Typography component="span" variant="body2" color="text.secondary">/ {card.quota} Used</Typography>
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          my: 1,
                          bgcolor: '#e2e8f0',
                          '& .MuiLinearProgress-bar': { bgcolor: card.color }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">{card.desc}</Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Leave History Table */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              All Leave Applications for this Employee
            </Typography>
            {leavesList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No leave applications found for this user.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><b>Type</b></TableCell>
                      <TableCell><b>Dates / Duration</b></TableCell>
                      <TableCell><b>Days</b></TableCell>
                      <TableCell><b>Reason</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Admin Feedback</b></TableCell>
                      <TableCell><b>Applied Date</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leavesList.map(leave => {
                      const datesArr = leave.dates && leave.dates.length > 0 ? leave.dates : [];
                      const formattedDates = datesArr.length > 0
                        ? datesArr.map(d => moment(d).format('DD MMM YYYY')).join(', ')
                        : (leave.startDate ? `${moment(leave.startDate).format('DD MMM')} - ${moment(leave.endDate).format('DD MMM YYYY')}` : '-');

                      return (
                        <TableRow key={leave._id} hover>
                          <TableCell>
                            <Chip
                              label={leave.type === 'Earned' || leave.type === 'Paid' ? 'Planned' : leave.type}
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                bgcolor:
                                  leave.type === 'Planned' || leave.type === 'Paid' || leave.type === 'Earned' ? '#e8f5e9' :
                                  leave.type === 'Sick' ? '#ffebee' : '#fff3e0',
                                color:
                                  leave.type === 'Planned' || leave.type === 'Paid' || leave.type === 'Earned' ? '#2e7d32' :
                                  leave.type === 'Sick' ? '#c62828' : '#e65100'
                              }}
                            />
                          </TableCell>
                          <TableCell>{formattedDates}</TableCell>
                          <TableCell><b>{datesArr.length || 1}</b></TableCell>
                          <TableCell>{leave.reason || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={leave.status}
                              size="small"
                              color={
                                leave.status === 'Approved' ? 'success' :
                                leave.status === 'Rejected' ? 'error' :
                                leave.status === 'Cancelled' ? 'default' : 'warning'
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ color: leave.status === 'Rejected' ? 'error.main' : 'inherit' }}>
                            {leave.rejectionReason || '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {leave.createdAt ? moment(leave.createdAt).format('DD MMM YYYY, hh:mm A') : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* TAB 2: BREAK LOG & TIMELINE */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Chronological Break Activity Log ({rangeLabel})
            </Typography>
            {allBreakSessions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FreeBreakfast sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                <Typography color="text.secondary">No break activities recorded during this period.</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Break Type</b></TableCell>
                      <TableCell><b>Start Time</b></TableCell>
                      <TableCell><b>End Time</b></TableCell>
                      <TableCell><b>Duration</b></TableCell>
                      <TableCell><b>Compliance Status</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allBreakSessions.map((b, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {moment(b.date).format('DD MMM YYYY (ddd)')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={b.type}
                            size="small"
                            sx={{
                              bgcolor: b.type.includes('Lunch') ? '#fff3e0' : '#e1f5fe',
                              color: b.type.includes('Lunch') ? '#e65100' : '#0277bd',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>{b.start ? moment(b.start).format('hh:mm:ss A') : '-'}</TableCell>
                        <TableCell>{b.end ? moment(b.end).format('hh:mm:ss A') : (b.isOngoing ? 'Ongoing...' : '-')}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: b.isOvertime ? '#d32f2f' : '#2e7d32' }}>
                            {formatDuration(b.duration)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {b.isOngoing ? (
                            <Chip label="In Progress" size="small" color="primary" variant="outlined" />
                          ) : b.isOvertime ? (
                            <Chip label="Exceeded Limit" size="small" color="error" variant="outlined" />
                          ) : (
                            <Chip label="On Time" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UserSummaryContent;
